/**
 * Innovation Hub › Tools › Test ask card (admin 0.54.0, pcb-innovation-api 0.13.0; Bill 2026-11-03). Mirrors the Loans
 * TestCardPage (LOP, pcb-loans-api 0.199.0).
 *
 *   POST v3/hub/admin/tools/test-card              (tools.testCard, reason required)   → { askId, token, sentTo, code: SENT | NOT_DELIVERED, message }
 *                                                   409 NOT_INSTALLED (no chat with the bot: nothing created) · 429 TOO_MANY_OPEN
 *   GET  v3/hub/admin/tools/test-card              (tools.testCard.list)               → { sentTo, installed, code, problem, maxOpen, items }
 *   POST v3/hub/admin/tools/test-card/{id}/cancel  (tools.testCard.cancel, reason required)
 *
 * The API creates a real test ask (kind 'bot-test', token TEST-{n}) and the Innovation Chat Bot sends its card to the signed-in
 * admin's own Teams account. Claim, Release, Mark done and Decline in Teams go through the normal in-card path; only the test
 * changes, nobody else is told, and the test is in no listing. While a card is open this page re-reads every 5 seconds.
 */

import { useCallback, useEffect, useState } from 'react';
import { EmptyState, PageHeader, StatusChip, type StatusTone } from '@retail-ops/panel-shell';
import { Banner, Button, Card, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { HubApiError, hubApi, type HubTestCard, type HubTestCardList } from './hubApi';

const POLL_MS = 5000;

const OUTCOME_TONE: Record<string, StatusTone> = {
  Open: 'open',
  Claimed: 'watch',
  Done: 'ok',
  Declined: 'problem',
  Cancelled: 'neutral',
  Expired: 'neutral',
};

/** Still waiting for a press (open or claimed, not expired). */
export function isLive(c: HubTestCard): boolean {
  return c.outcome === 'Open' || c.outcome === 'Claimed';
}

/** The "How" column: what happened to the card, in plain words. */
export function howText(c: HubTestCard): string {
  if (isLive(c)) {
    if (c.deliveryStatus === 'failed') return 'The card did not reach Teams';
    return c.outcome === 'Claimed' ? 'Claimed in Teams; waiting for Mark done or Decline' : 'Waiting for your press in Teams';
  }
  if (c.outcome === 'Done' || c.outcome === 'Declined') return c.channel === 'card' ? 'Pressed in Teams' : c.channel ? `Answered in the ${c.channel}` : 'Answered';
  if (c.outcome === 'Expired') return 'Not answered within 24 hours';
  return c.decision ? `Cancelled: ${c.decision}` : 'Cancelled';
}

/** What the page shows after a send: sent, not installed, or failed with the reason. */
export interface HubSendResult {
  tone: 'ok' | 'not-installed' | 'failed';
  message: string;
}

export function hubSendResultFrom(e: unknown): HubSendResult {
  if (e instanceof HubApiError && e.code === 'NOT_INSTALLED') return { tone: 'not-installed', message: e.message };
  return { tone: 'failed', message: e instanceof Error ? (e instanceof HubApiError && e.code ? `${e.message} (${e.code})` : e.message) : String(e) };
}

function fmtCt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })} CT`;
}

export function HubTestCardPage(): JSX.Element {
  const { status } = usePlatforms();
  const canCancel = status.hub.kind === 'ready' && status.hub.caps.actions.some((a) => a.id === 'tools.testCard.cancel');

  const [data, setData] = useState<HubTestCardList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [result, setResult] = useState<HubSendResult | null>(null);

  const [dialog, setDialog] = useState<{ kind: 'send' } | { kind: 'cancel'; card: HubTestCard } | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await hubApi.testCards());
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anyLive = (data?.items ?? []).some(isLive);
  useEffect(() => {
    if (!anyLive) return undefined;
    const t = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(t);
  }, [anyLive, load]);

  const openDialog = (d: NonNullable<typeof dialog>): void => {
    setReason(d.kind === 'send' ? 'Checking the Innovation Chat Bot' : '');
    setTried(false);
    setSaveError(null);
    setDialog(d);
  };
  const closeDialog = (): void => {
    if (!saving) setDialog(null);
  };

  const submit = async (): Promise<void> => {
    if (!dialog) return;
    setTried(true);
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (dialog.kind === 'send') {
        const r = await hubApi.sendTestCard(asciiReason(reason));
        setResult({ tone: r.code === 'SENT' ? 'ok' : 'failed', message: r.message });
      } else {
        const r = await hubApi.cancelTestCard(dialog.card.askId, asciiReason(reason));
        setResult({ tone: 'ok', message: r.message });
      }
      setDialog(null);
    } catch (e) {
      const r = hubSendResultFrom(e);
      // A refusal that is about the bot (not this dialog) belongs on the page, not in the dialog.
      if (dialog.kind === 'send' && r.tone === 'not-installed') {
        setResult(r);
        setDialog(null);
      } else {
        setSaveError(r.message);
      }
    } finally {
      setSaving(false);
      await load();
    }
  };

  const items = data?.items ?? [];
  const openCount = items.filter(isLive).length;
  const atLimit = data ? openCount >= data.maxOpen : false;
  const notInstalled = data !== null && !data.installed;

  const columns: ReadonlyArray<DataTableColumn<HubTestCard>> = [
    {
      header: 'Test card',
      cell: (c) => (
        <>
          <span className="cell-strong">{c.token ?? `#${c.askId}`}</span>
          <div className="cell-desc">Sent {fmtCt(c.requestedAt)}</div>
        </>
      ),
      sortKey: (c) => c.askId,
      cardTitle: true,
    },
    { header: 'Outcome', cell: (c) => <StatusChip tone={OUTCOME_TONE[c.outcome] ?? 'neutral'}>{c.outcome}</StatusChip>, sortKey: (c) => c.outcome },
    { header: 'When', cell: (c) => fmtCt(c.settledAt ?? c.claimedAt), sortKey: (c) => c.settledAt ?? c.claimedAt ?? '' },
    {
      header: 'How',
      cell: (c) => (
        <>
          {howText(c)}
          {c.comment && <div className="cell-desc">&ldquo;{c.comment}&rdquo;</div>}
          {isLive(c) && c.deliveryStatus === 'failed' && c.deliveryDetail && <div className="cell-desc">{c.deliveryDetail}</div>}
        </>
      ),
    },
    {
      header: '',
      cell: (c) =>
        isLive(c) && canCancel ? (
          <Button variant="ghost" size="sm" onClick={() => openDialog({ kind: 'cancel', card: c })}>
            Cancel
          </Button>
        ) : (
          <></>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Test ask card"
        intro={
          <>
            Sends a real ask card from the Innovation Chat Bot to your own Teams account{data?.sentTo ? ` (${data.sentTo})` : ''}. Press
            Claim, Release, Mark done or Decline in Teams: the card redraws exactly as a real ask does, but only this test changes,
            nobody else is told, and the test never shows in any list or monitor. At most {data?.maxOpen ?? 3} can be open at a time; an
            unanswered one expires after 24 hours.
          </>
        }
        actions={
          <Button onClick={() => openDialog({ kind: 'send' })} disabled={!data || notInstalled || atLimit}>
            Send me a test ask card
          </Button>
        }
      />

      {notInstalled && data?.problem && (
        <Banner tone="warn">
          <strong>{data.code === 'NOT_INSTALLED' ? 'Not installed.' : 'The bot cannot send cards.'}</strong> {data.problem}
        </Banner>
      )}
      {atLimit && (
        <Banner tone="info">
          You have {openCount} open test cards. Answer one in Teams{canCancel ? ' or cancel one below' : ''} before sending another.
        </Banner>
      )}
      {result && (
        <Banner tone={result.tone === 'ok' ? 'info' : result.tone === 'not-installed' ? 'warn' : 'err'}>
          <strong>{result.tone === 'ok' ? 'Sent.' : result.tone === 'not-installed' ? 'Not installed.' : 'Failed.'}</strong> {result.message}{' '}
          <Button variant="ghost" size="sm" onClick={() => setResult(null)}>
            Dismiss
          </Button>
        </Banner>
      )}
      {loadError && (
        <Banner tone="err">
          Could not load your test cards: {loadError}{' '}
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </Banner>
      )}

      {!data && !loadError ? (
        <Spinner label="Loading your test cards…" />
      ) : (
        <Card title={anyLive ? 'Your recent test cards — checking for your press every few seconds' : 'Your recent test cards'}>
          <DataTable<HubTestCard>
            columns={columns}
            rows={items}
            rowKey={(c) => String(c.askId)}
            emptyState={<EmptyState title="You have not sent a test ask card yet.">Use the button above to send one to yourself.</EmptyState>}
          />
        </Card>
      )}

      <ModalShell
        open={dialog !== null}
        title={dialog?.kind === 'cancel' ? `Cancel ${dialog.card.token ?? `#${dialog.card.askId}`}?` : 'Send me a test ask card'}
        onClose={closeDialog}
        width={560}
        actions={
          <>
            <Button variant="ghost" onClick={closeDialog} disabled={saving}>
              Back
            </Button>
            <Button onClick={() => void submit()} disabled={saving}>
              {saving ? 'Working…' : dialog?.kind === 'cancel' ? 'Cancel the test card' : 'Send the card'}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          {saveError && <Banner tone="err">{saveError}</Banner>}
          <p>
            {dialog?.kind === 'cancel'
              ? 'The card in Teams changes to say it was cancelled. Nothing else happens.'
              : `The Innovation Chat Bot sends the card to ${data?.sentTo ?? 'your Teams account'}. It says it is a test, shows your reason, and has Claim, Mark done and Decline buttons (Decline asks for a reason).`}
          </p>
          <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
        </div>
      </ModalShell>
    </div>
  );
}
